/**
 * App entry. Boot order, mirroring the Android launch flow:
 *   1. Resolve persisted Settings.
 *   2. If onboarding incomplete → run wizard.
 *   3. If pipeline run requested via share-target POST → handle that source.
 *   4. Otherwise: render entry screen (dropzone, file picker, share hint,
 *      installed-model summary, settings shortcut).
 *   5. On image input: warm up models (with progress) → run pipeline →
 *      mount editor canvas → wire share / re-detect / settings buttons.
 */

import { RedactionCanvas } from "./canvas.js";
import { recognize, type OcrStage, type OcrTokenBox } from "./ocr.js";
import { findSpans, isVariantCached, loadPii } from "./pii.js";
import { mapSpansToRects } from "./spanmapper.js";
import { exportToBlob, shareOrDownload } from "./share.js";
import {
  isOnboardingCompleted,
  markOnboardingCompleted,
  runOnboarding,
  runWelcome,
  warmupVariant,
} from "./onboarding.js";
import { isLegalAccepted, renderLegalDeclined, runLegalWaiver } from "./legal.js";
import { getRuntimeStatus, requestPersistentStorage, summariseEPs, type RuntimeStatus } from "./runtime.js";
import { loadSettings, saveSettings, argbToHex, hexToArgb, type SettingsValues, type VariantId, type BgMode } from "./settings.js";
import { VARIANTS, humanBytes } from "./models.js";

interface PipelineResult {
  text: string;
  rects: ReturnType<typeof mapSpansToRects>;
  bitmapCanvas: HTMLCanvasElement;
  bitmapImageData: ImageData;
  tokens: OcrTokenBox[];
}

let canvas: RedactionCanvas | null = null;
let pipelineResult: PipelineResult | null = null;

/**
 * Tracks an in-flight or completed background warm-up of the active
 * privacy-filter variant. We kick this off the moment the entry screen
 * mounts so the IndexedDB read + WebGPU shader compile happens while
 * the user is still looking at the dropzone — by the time they actually
 * pick an image it's typically hot. The variant id is recorded so we
 * can detect a settings-change and re-warm.
 */
const prewarm: {
  variantId: VariantId | null;
  status: "idle" | "loading" | "ready" | "failed";
  /** Sub-stage when status === "loading". */
  phase: "tokenizer" | "downloading" | "compiling" | null;
  /** 0..1 download progress for the current weight file, when known. */
  progress: number | null;
  error?: string;
  /** UI elements that want to be told when status flips. */
  listeners: Array<() => void>;
} = { variantId: null, status: "idle", phase: null, progress: null, listeners: [] };

function notifyPrewarm(): void {
  for (const l of prewarm.listeners) {
    try { l(); } catch { /* ignore */ }
  }
}

function startPrewarm(variantId: VariantId): void {
  if (prewarm.variantId === variantId && (prewarm.status === "loading" || prewarm.status === "ready")) {
    return; // Already warming or warmed for this variant.
  }
  prewarm.variantId = variantId;
  prewarm.status = "loading";
  prewarm.phase = "tokenizer";
  prewarm.progress = null;
  prewarm.error = undefined;
  notifyPrewarm();
  void loadPii(variantId, (p) => {
    if (prewarm.variantId !== variantId) return;
    if (p.stage === "fetch_tokenizer") {
      prewarm.phase = "tokenizer";
      prewarm.progress = null;
    } else if (p.stage === "fetch_graph" || p.stage === "fetch_weights") {
      prewarm.phase = "downloading";
      prewarm.progress = p.total > 0 ? Math.min(1, p.loaded / p.total) : null;
    } else if (p.stage === "compiling") {
      prewarm.phase = "compiling";
      prewarm.progress = null;
    } else if (p.stage === "ready") {
      prewarm.phase = null;
    }
    notifyPrewarm();
  }).then(
    () => {
      if (prewarm.variantId === variantId) {
        prewarm.status = "ready";
        prewarm.phase = null;
        prewarm.progress = null;
        notifyPrewarm();
      }
    },
    (e: unknown) => {
      if (prewarm.variantId === variantId) {
        prewarm.status = "failed";
        prewarm.phase = null;
        prewarm.progress = null;
        prewarm.error = String((e as Error)?.message ?? e);
        notifyPrewarm();
      }
    },
  );
}

// `<script type="module">` is implicitly deferred — it runs after the
// document has been parsed, so DOMContentLoaded has already fired and
// adding a listener for it would be a no-op. Boot directly.
(window as unknown as { __redactBoot?: string }).__redactBoot = "starting";
boot().then(
  () => {
    (window as unknown as { __redactBoot?: string }).__redactBoot = "done";
  },
  (err: unknown) => {
    (window as unknown as { __redactBootErr?: string }).__redactBootErr = String((err as Error)?.stack ?? err);
  },
);

async function boot(): Promise<void> {
  const root = document.getElementById("app");
  if (!root) throw new Error("Missing #app mount");

  // Optional service worker — caches the privacy-filter model bytes via
  // the Cache API instead of the browser's HTTP cache. Off by default:
  // the IDB-vs-SW benchmark (web/public/sw.js) didn't show a meaningful
  // win once the rest of the stack moved to the asyncify wasm and warm
  // loads dropped to ~2 s — byte streaming is no longer the bottleneck.
  // Kept around so we can measure it again on slower hardware. Enable
  // with `localStorage.redact_use_sw = "1"`.
  if ("serviceWorker" in navigator) {
    let useSw = false;
    try { useSw = localStorage.getItem("redact_use_sw") === "1"; } catch { /* ignore */ }
    if (useSw) {
      navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => undefined);
    } else {
      // If a stale registration exists from a previous A/B run, drop it
      // so the default path is actually the default.
      navigator.serviceWorker.getRegistrations()
        .then((rs) => rs.forEach((r) => r.unregister()))
        .catch(() => undefined);
    }
  }

  // First-launch flow:
  //   1. Welcome explainer (no step counter — sets context).
  //   2. Step 1 of 3: legal waiver (four acknowledgements).
  //   3. Step 2 of 3: pick a privacy filter variant.
  //   4. Step 3 of 3: all-set summary.
  // The first three persist into localStorage so subsequent visits
  // jump straight to the entry screen.
  const needsWelcome = !isLegalAccepted() && !isOnboardingCompleted();
  if (needsWelcome) {
    await runWelcome(root);
  }

  // Legal waiver — first acknowledgement step. If the user declines we
  // hold them on a friendly wall and let them retry from there.
  const wasAccepted = isLegalAccepted();
  while (!isLegalAccepted()) {
    try {
      await runLegalWaiver({ root });
    } catch {
      await new Promise<void>((resolve) => {
        renderLegalDeclined(root, resolve);
      });
    }
  }
  // Right after first acceptance, ask the browser to mark storage
  // persistent. Some browsers auto-grant from engagement heuristics
  // (Chrome PWAs, frequent visits); Firefox prompts; Safari no-ops.
  // Without this, the cached 800 MB privacy-filter model is fair game
  // for storage-pressure eviction and Safari's 7-day non-persistent
  // GC. Best-effort only — never throws, never blocks the UI.
  if (!wasAccepted) {
    void requestPersistentStorage();
  }

  if (!isOnboardingCompleted()) {
    const result = await runOnboarding({ root });
    const s = loadSettings();
    s.variantId = result.variantId;
    saveSettings(s);
    markOnboardingCompleted();
  }

  renderEntry(root);
}

/* -------------------------------------------------------------------------- */
/* Entry screen — drop zone, share hint, model summary, settings shortcut.    */
/* -------------------------------------------------------------------------- */

function renderEntry(root: HTMLElement): void {
  const settings = loadSettings();
  root.innerHTML = "";

  const shell = document.createElement("div");
  shell.className = "entry-shell";

  shell.appendChild(renderTopBar(() => openSettings(root, () => renderEntry(root))));

  const main = document.createElement("main");
  main.className = "entry-main";

  const dropzone = renderDropzone(async (file) => {
    void runPipelineAndShowEditor(root, file, settings);
  });
  main.appendChild(dropzone);

  // Only show the model status card when there's something to say —
  // i.e. the active variant isn't cached yet. Once it's downloaded, the
  // entry screen is just dropzone + "Settings", no chrome.
  if (!isVariantCached(settings.variantId)) {
    main.appendChild(renderModelStatus(settings));
  }

  shell.appendChild(main);
  root.appendChild(shell);

  // Pre-warm the cached variant. The IndexedDB read + WebGPU compile is
  // the bulk of "Warming up the cached model" — running it here means
  // the user almost never has to wait for it explicitly.
  startPrewarm(settings.variantId);
}

function renderTopBar(onSettings: () => void): HTMLElement {
  const bar = document.createElement("header");
  bar.className = "topbar";
  bar.innerHTML = `<h1 class="title serif">Redact</h1>`;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "pill pill-ghost";
  btn.textContent = "Settings";
  btn.addEventListener("click", onSettings);
  bar.appendChild(btn);
  return bar;
}

function renderDropzone(onFile: (file: File) => void): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "entry-zone";
  wrap.innerHTML = `
    <p class="serif entry-zone-title">Drop a screenshot here, or pick one.</p>
    <div class="entry-zone-actions">
      <label class="pill pill-primary">
        Pick an image
        <input id="file-input" type="file" accept="image/*" hidden />
      </label>
      <p class="entry-zone-hint">On mobile, install this page and share images to it from any app.</p>
    </div>
  `;

  const input = wrap.querySelector<HTMLInputElement>("#file-input")!;
  input.addEventListener("change", () => {
    const f = input.files?.[0];
    if (f) onFile(f);
    input.value = "";
  });

  ["dragover", "dragenter"].forEach((evt) =>
    wrap.addEventListener(evt, (e) => {
      e.preventDefault();
      wrap.classList.add("drag");
    }),
  );
  ["dragleave", "dragend", "drop"].forEach((evt) =>
    wrap.addEventListener(evt, () => wrap.classList.remove("drag")),
  );
  wrap.addEventListener("drop", (e) => {
    e.preventDefault();
    const dt = (e as DragEvent).dataTransfer;
    const f = dt?.files?.[0];
    if (f && f.type.startsWith("image/")) onFile(f);
  });

  // Paste from clipboard.
  window.addEventListener("paste", (e) => {
    const items = (e as ClipboardEvent).clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) onFile(f);
      }
    }
  });

  return wrap;
}

function renderModelStatus(settings: SettingsValues): HTMLElement {
  const card = document.createElement("section");
  card.className = "entry-card";
  const cached = isVariantCached(settings.variantId);

  const head = document.createElement("div");
  head.className = "entry-card-head";
  head.innerHTML = `
    <h2 class="serif">Privacy filter</h2>
    <span class="entry-card-status" id="entry-card-status">${cached ? "Installed" : "Will download on first use"}</span>
  `;
  card.appendChild(head);

  const others = VARIANTS.filter((x) => isVariantCached(x.id) && x.id !== settings.variantId);
  if (others.length > 0) {
    const line = document.createElement("p");
    line.className = "entry-card-meta";
    line.textContent = `Also cached: ${others.map((x) => x.displayName).join(", ")}`;
    card.appendChild(line);
  }

  // Live prewarm status — flips between "warming…" and "ready" as the
  // background loadPii Promise resolves. Subscribed once per render
  // and torn down when the card leaves the DOM.
  const statusEl = card.querySelector<HTMLSpanElement>("#entry-card-status");
  if (statusEl) {
    const refresh = () => {
      if (prewarm.variantId !== settings.variantId) return;
      if (prewarm.status === "loading") {
        // Phase-aware copy so a 70-second cold download doesn't read
        // "Will download on first use" the entire time.
        let txt: string;
        if (prewarm.phase === "downloading") {
          const pct = prewarm.progress != null ? Math.round(prewarm.progress * 100) : null;
          const verb = cached ? "Loading" : "Downloading";
          txt = pct != null ? `${verb}… ${pct}%` : `${verb}…`;
        } else if (prewarm.phase === "compiling") {
          txt = "Compiling…";
        } else if (prewarm.phase === "tokenizer") {
          txt = "Loading tokenizer…";
        } else {
          txt = cached ? "Warming up…" : "Preparing…";
        }
        statusEl.textContent = txt;
        statusEl.classList.add("entry-card-status-warming");
        statusEl.classList.remove("entry-card-status-ready");
      } else if (prewarm.status === "ready") {
        // Once the download+compile completes, the card has nothing to
        // tell the user — the model is on disk and warm. Remove it so
        // the entry screen quiets down.
        card.remove();
      } else if (prewarm.status === "failed") {
        statusEl.textContent = "Warm-up failed";
        statusEl.classList.remove("entry-card-status-warming", "entry-card-status-ready");
      }
    };
    refresh();
    prewarm.listeners.push(refresh);
    // Clean up when the card is removed (renderEntry replaces #app
    // wholesale, so a MutationObserver isn't reliable; use a
    // best-effort sweep — stale listeners are no-ops).
  }

  return card;
}

/* -------------------------------------------------------------------------- */
/* Pipeline run + editor mount.                                               */
/* -------------------------------------------------------------------------- */

/**
 * Run OCR + privacy-filter on a previously-decoded canvas. Used for the
 * initial pick-image flow and for Re-detect on the editor — both want
 * the same loading explainer chrome and stage progression.
 */
async function runDetection(
  root: HTMLElement,
  bitmapCanvas: HTMLCanvasElement,
  settings: SettingsValues,
  opts: { sourceLabel: string },
): Promise<{ text: string; rects: ReturnType<typeof mapSpansToRects>; tokens: OcrTokenBox[] }> {
  // 1. Privacy filter warm-up. The entry screen already kicked off
  //    `loadPii` in the background; if it's done we skip the overlay
  //    entirely. If it's still running we just show a brief "warming"
  //    state and await the same Promise. First-launch download still
  //    goes through `warmupVariant` (with byte-level progress).
  if (!isVariantCached(settings.variantId)) {
    const handle = warmupVariant(root, settings.variantId);
    await handle.done;
  } else if (prewarm.variantId === settings.variantId && prewarm.status === "ready") {
    // No overlay — the model is already hot from the entry screen pre-warm.
  } else {
    renderLoadingOverlay(root, "Loading the privacy filter", "Warming the cached model up for inference.", { mode: "indeterminate" });
    await loadPii(settings.variantId);
  }

  // 2. OCR. recognize()'s listener gives us per-stage feedback we map to
  //    the explainer copy.
  renderLoadingOverlay(root, "Reading the image", `Looking at ${opts.sourceLabel}.`, { mode: "indeterminate" });
  const ocr = await recognize(bitmapCanvas, (s: OcrStage) => {
    if (s.stage === "loading_worker") {
      updateLoadingOverlay(root, "Loading the recogniser", "First-time download of the OCR worker.", { mode: "indeterminate" });
    } else if (s.stage === "loading_lang") {
      updateLoadingOverlay(root, "Loading English", "Caching eng.traineddata for next time.", { mode: "indeterminate" });
    } else if (s.stage === "recognizing") {
      updateLoadingOverlay(root, "Reading text", `Recognising the bitmap… ${Math.round(s.progress * 100)}%`, { mode: "determinate", progress: s.progress });
    }
  });

  // 3. Privacy-filter inference. Single-pass token classifier — no
  //    incremental progress, but show indeterminate motion so it doesn't
  //    look like the page is wedged between OCR-100% and the editor.
  renderLoadingOverlay(
    root,
    "Identifying personal info",
    `Privacy filter is scanning ${ocr.tokens.length} OCR tokens for sensitive spans.`,
    { mode: "indeterminate" },
  );
  const spans = await findSpans(ocr.text, settings.variantId);
  const rects = mapSpansToRects(ocr.tokens, spans, bitmapCanvas.width, bitmapCanvas.height);

  return { text: ocr.text, rects, tokens: ocr.tokens };
}

async function runPipelineAndShowEditor(
  root: HTMLElement,
  file: File,
  settings: SettingsValues,
): Promise<void> {
  // Decode the source image into a canvas + ImageData up front so the
  // overlay has dimensions to render against (and so we keep working on
  // a stable copy if Re-detect runs later).
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (e) {
    showError(root, `Couldn't decode that image: ${(e as Error).message}`);
    return;
  }
  const bitmapCanvas = document.createElement("canvas");
  bitmapCanvas.width = bitmap.width;
  bitmapCanvas.height = bitmap.height;
  const bctx = bitmapCanvas.getContext("2d");
  if (!bctx) {
    showError(root, "Couldn't get a 2d context for this image.");
    return;
  }
  bctx.drawImage(bitmap, 0, 0);
  const bitmapImageData = bctx.getImageData(0, 0, bitmap.width, bitmap.height);
  bitmap.close();

  renderLoadingOverlay(root, "Preparing", `Picking up ${file.name || "your image"}.`, { mode: "indeterminate" });

  let result: { text: string; rects: ReturnType<typeof mapSpansToRects>; tokens: OcrTokenBox[] };
  try {
    result = await runDetection(root, bitmapCanvas, settings, {
      sourceLabel: file.name || "your image",
    });
  } catch (e) {
    showError(root, `Pipeline failed: ${(e as Error).message}`);
    return;
  }

  pipelineResult = { text: result.text, rects: result.rects, tokens: result.tokens, bitmapCanvas, bitmapImageData };
  mountEditor(root, settings);
}

interface OverlayOpts {
  mode: "indeterminate" | "determinate";
  /** 0..1 when mode is determinate. */
  progress?: number;
}

function renderLoadingOverlay(root: HTMLElement, title: string, body: string, opts: OverlayOpts): void {
  root.innerHTML = "";
  const wrap = document.createElement("div");
  wrap.className = "warmup-shell";
  const isDet = opts.mode === "determinate";
  const pct = Math.round((opts.progress ?? 0) * 100);
  wrap.innerHTML = `
    <div class="warmup-card">
      <h2 class="serif warmup-title" id="warmup-title">${escapeHtml(title)}</h2>
      <p class="warmup-body" id="warmup-stage">${escapeHtml(body)}</p>
      <div class="warmup-progress${isDet ? "" : " warmup-progress-indet"}">
        <div id="warmup-bar" style="${isDet ? `width:${pct}%` : ""}"></div>
      </div>
    </div>
  `;
  root.appendChild(wrap);
}

function updateLoadingOverlay(
  root: HTMLElement,
  title: string,
  body: string,
  opts: OverlayOpts,
): void {
  const wrap = root.querySelector<HTMLDivElement>(".warmup-shell");
  if (!wrap) {
    renderLoadingOverlay(root, title, body, opts);
    return;
  }
  const t = wrap.querySelector<HTMLHeadingElement>("#warmup-title");
  const b = wrap.querySelector<HTMLParagraphElement>("#warmup-stage");
  if (t) t.textContent = title;
  if (b) b.textContent = body;
  const progress = wrap.querySelector<HTMLDivElement>(".warmup-progress");
  const bar = wrap.querySelector<HTMLDivElement>("#warmup-bar");
  if (progress && bar) {
    if (opts.mode === "determinate") {
      progress.classList.remove("warmup-progress-indet");
      bar.style.width = `${Math.round((opts.progress ?? 0) * 100)}%`;
    } else {
      progress.classList.add("warmup-progress-indet");
      bar.style.width = "";
    }
  }
}

function mountEditor(root: HTMLElement, settings: SettingsValues): void {
  if (!pipelineResult) return;
  root.innerHTML = "";

  const shell = document.createElement("div");
  shell.className = "editor-shell";
  // Single-column layout — no right sidebar. Topbar carries the title plus
  // the editor's chrome buttons. Selection-only actions (Delete, Fit view)
  // live inside canvas-wrap as small, contextual overlays so they don't
  // steal focus when nothing is selected.
  shell.innerHTML = `
    <header class="editor-topbar">
      <button id="back" type="button" class="iconbtn" aria-label="New image" title="New image">←</button>
      <h1 class="editor-title serif">Redact</h1>
      <div class="editor-topbar-right">
        <button id="redetect" type="button" class="pill pill-ghost">Re-detect</button>
        <button id="settings-btn" type="button" class="iconbtn" aria-label="Settings" title="Settings">⚙</button>
      </div>
    </header>
    <main class="editor-main editor-main-single">
      <div class="canvas-wrap">
        <canvas id="canvas"></canvas>
        <button id="reset-view" type="button" class="canvas-chip canvas-chip-tl" aria-label="Fit view" title="Fit view">⤢</button>
        <button id="delete-sel" type="button" class="canvas-chip canvas-chip-tr canvas-chip-danger" hidden>Delete selection</button>
        <button id="add-fab" class="fab fab-add" type="button" aria-label="Add redaction">+</button>
        <button id="share-fab" class="fab fab-share" type="button" aria-label="Share">Share</button>
        <div id="status" class="canvas-status"></div>
      </div>
    </main>
  `;
  root.appendChild(shell);

  const canvasEl = shell.querySelector<HTMLCanvasElement>("#canvas")!;
  const status = shell.querySelector<HTMLDivElement>("#status")!;
  const addFab = shell.querySelector<HTMLButtonElement>("#add-fab")!;
  const shareFab = shell.querySelector<HTMLButtonElement>("#share-fab")!;
  const deleteBtn = shell.querySelector<HTMLButtonElement>("#delete-sel")!;
  const resetBtn = shell.querySelector<HTMLButtonElement>("#reset-view")!;
  const redetectBtn = shell.querySelector<HTMLButtonElement>("#redetect")!;
  const settingsBtn = shell.querySelector<HTMLButtonElement>("#settings-btn")!;
  const backBtn = shell.querySelector<HTMLButtonElement>("#back")!;

  canvas = new RedactionCanvas(canvasEl, {
    onSelectionChanged: (sel) => {
      deleteBtn.hidden = !sel;
    },
    onArmStateChanged: (armed) => {
      addFab.classList.toggle("armed", armed);
    },
  });
  canvas.setImage(pipelineResult.bitmapCanvas, [...pipelineResult.rects]);
  canvas.applySettings(settings.bgMode, settings.bgColor, settings.previewAlpha);
  canvas.setDebugOcr(pipelineResult.tokens, pipelineResult.text, settings.debugShowOcr);
  const setStatus = (text: string) => { status.textContent = text; };

  addFab.addEventListener("click", () => canvas?.armNextDrag());
  resetBtn.addEventListener("click", () => canvas?.fitView());
  deleteBtn.addEventListener("click", () => canvas?.deleteSelected());
  backBtn.addEventListener("click", () => {
    pipelineResult = null;
    canvas = null;
    renderEntry(root);
  });
  settingsBtn.addEventListener("click", () => {
    openSettings(root, () => {
      // On close, re-render the editor with the latest settings so any
      // fill-mode / preview-alpha change takes effect immediately.
      const next = loadSettings();
      mountEditor(root, next);
    });
  });

  redetectBtn.addEventListener("click", async () => {
    if (!pipelineResult || !canvas) return;
    const stash = pipelineResult;
    try {
      const result = await runDetection(root, stash.bitmapCanvas, settings, {
        sourceLabel: "the same image",
      });
      pipelineResult = { ...stash, text: result.text, rects: result.rects, tokens: result.tokens };
      mountEditor(root, settings);
    } catch (e) {
      pipelineResult = stash;
      mountEditor(root, settings);
      const s = document.getElementById("status");
      if (s) s.textContent = `Re-detect failed: ${(e as Error).message}`;
    }
  });

  shareFab.addEventListener("click", async () => {
    if (!pipelineResult || !canvas) return;
    const cur = loadSettings();
    const scale = cur.resizeBeforeShare ? cur.resizeSharePercent : 100;
    shareFab.disabled = true;
    try {
      const blob = await exportToBlob({
        source: pipelineResult.bitmapCanvas,
        imageData: pipelineResult.bitmapImageData,
        rects: canvas.currentRects(),
        scalePercent: scale,
      });
      await shareOrDownload(blob, `redacted-${stamp()}.png`);
    } catch (e) {
      setStatus(`Share failed: ${(e as Error).message}`);
    } finally {
      shareFab.disabled = false;
    }
  });
}

function showError(root: HTMLElement, msg: string): void {
  root.innerHTML = "";
  const wrap = document.createElement("div");
  wrap.className = "warmup-shell";
  wrap.innerHTML = `
    <div class="warmup-card warmup-card-error">
      <h2 class="serif warmup-title">Something went wrong</h2>
      <p class="warmup-body">${escapeHtml(msg)}</p>
      <button id="back" type="button" class="pill pill-primary">Try again</button>
    </div>
  `;
  root.appendChild(wrap);
  wrap.querySelector<HTMLButtonElement>("#back")!.addEventListener("click", () => {
    renderEntry(root);
  });
}

/* -------------------------------------------------------------------------- */
/* Settings — full-screen tabbed view, mirrors new-design/screens.jsx.        */
/* -------------------------------------------------------------------------- */

type SettingsTab = "model" | "editing" | "info";

function openSettings(root: HTMLElement, onClose: () => void): void {
  let activeTab: SettingsTab = "model";
  const render = () => {
    const settings = loadSettings();
    root.innerHTML = "";
    const shell = document.createElement("div");
    shell.className = "settings-shell";
    shell.innerHTML = `
      <header class="settings-topbar">
        <button class="iconbtn" id="back" type="button" aria-label="Back">←</button>
        <h1 class="settings-title serif">Redact</h1>
        <span class="settings-topbar-spacer" aria-hidden="true"></span>
      </header>
      <nav class="settings-tabbar">
        <button class="settings-tab" data-tab="model"   type="button"><span>Model</span><i></i></button>
        <button class="settings-tab" data-tab="editing" type="button"><span>Editing</span><i></i></button>
        <button class="settings-tab" data-tab="info"    type="button"><span>Information</span><i></i></button>
      </nav>
      <div class="settings-body" id="settings-body"></div>
    `;
    root.appendChild(shell);

    shell.querySelectorAll<HTMLButtonElement>(".settings-tab").forEach((btn) => {
      const tab = btn.dataset.tab as SettingsTab;
      btn.classList.toggle("is-active", tab === activeTab);
      btn.addEventListener("click", () => {
        activeTab = tab;
        render();
      });
    });
    shell.querySelector<HTMLButtonElement>("#back")!.addEventListener("click", onClose);

    const body = shell.querySelector<HTMLDivElement>("#settings-body")!;
    if (activeTab === "model") body.appendChild(renderSettingsModel(settings, render));
    else if (activeTab === "editing") body.appendChild(renderSettingsEditing(settings));
    else body.appendChild(renderSettingsInfo());
  };
  render();
}

function eyebrow(text: string): HTMLElement {
  const e = document.createElement("div");
  e.className = "settings-eyebrow";
  e.textContent = text;
  return e;
}

function settingsCard(): HTMLElement {
  const c = document.createElement("section");
  c.className = "settings-card";
  return c;
}

function renderSettingsModel(s: SettingsValues, redraw: () => void): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "settings-tab-pane";

  wrap.appendChild(eyebrow("Privacy filter"));
  const pf = settingsCard();

  const variantOpts = VARIANTS.map((variant) => ({
    id: variant.id,
    label: variant.displayName,
    description: `${humanBytes(variant.totalBytes)} · ${variant.tagline}`,
    badge: isVariantCached(variant.id) ? "Installed" : variant.recommended ? "Recommended" : null,
  }));
  pf.appendChild(renderSelectorList(variantOpts, s.variantId, (id) => {
    saveSettings({ ...s, variantId: id as VariantId });
    redraw();
  }, { showBadges: true }));
  wrap.appendChild(pf);

  // Recogniser — single option today (Tesseract). Placeholder list so
  // the surface matches mobile parity if/when a second engine lands.
  wrap.appendChild(eyebrow("Recogniser"));
  const rec = settingsCard();
  rec.appendChild(renderSelectorList(
    [
      { id: "tesseract", label: "Tesseract.js", description: "English bundled. Browser-side OCR via WebAssembly." },
    ],
    "tesseract",
    () => undefined,
  ));
  wrap.appendChild(rec);

  return wrap;
}

function renderSettingsEditing(s: SettingsValues): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "settings-tab-pane";

  wrap.appendChild(eyebrow("Redaction"));
  const card = settingsCard();
  card.classList.add("settings-card-padded");
  const head = document.createElement("div");
  head.className = "settings-section-head";
  head.innerHTML = `
    <div class="settings-section-title serif">Redaction fill</div>
    <div class="settings-section-body">How new redactions are filled. The choice applies to rectangles you add after this point.</div>
  `;
  card.appendChild(head);

  const fillOpts = [
    { id: "DETECT",  label: "Match",   description: "Samples colours from around the rectangle so the patch blends back in." },
    { id: "BLACK",   label: "Black",   description: "Hard black fill. Fastest, makes it obvious that something was hidden." },
    { id: "COLOR",   label: "Colour",  description: "A solid colour you pick. Useful when the image already has a brand fill." },
    { id: "SHUFFLE", label: "Shuffle", description: "Scrambles the pixels under the rectangle. Destroys text, keeps the palette." },
  ];
  const fillList = renderSelectorList(fillOpts, s.bgMode, (id) => {
    saveSettings({ ...loadSettings(), bgMode: id as BgMode });
  });
  fillList.classList.add("settings-list-flush");
  card.appendChild(fillList);

  // Solid-colour picker, shown when bgMode === COLOR.
  const colourRow = document.createElement("div");
  colourRow.className = "settings-row settings-row-inline";
  colourRow.style.paddingTop = "16px";
  colourRow.innerHTML = `
    <div>
      <div class="settings-row-title serif">Fill colour</div>
      <div class="settings-row-meta">Used when fill is set to “Colour”.</div>
    </div>
    <input id="bg-color" type="color" />
  `;
  const colourInput = colourRow.querySelector<HTMLInputElement>("#bg-color")!;
  colourInput.value = argbToHex(s.bgColor);
  colourInput.addEventListener("change", () => {
    saveSettings({ ...loadSettings(), bgColor: hexToArgb(colourInput.value) });
  });
  card.appendChild(colourRow);

  // Preview transparency slider.
  const transparency = document.createElement("div");
  transparency.style.paddingTop = "22px";
  transparency.innerHTML = `
    <div class="settings-section-head settings-section-head-row">
      <div class="settings-section-title serif">Preview transparency</div>
      <div class="settings-section-body settings-section-body-mono" id="alpha-readout">${Math.round((s.previewAlpha / 255) * 100)}%</div>
    </div>
    <input id="alpha" type="range" min="0" max="255" step="1" value="${s.previewAlpha}" class="settings-slider" />
    <div class="settings-section-body" style="margin-top:10px">Lets you peek through redactions while editing. The exported image is always fully opaque.</div>
  `;
  const alpha = transparency.querySelector<HTMLInputElement>("#alpha")!;
  const readout = transparency.querySelector<HTMLDivElement>("#alpha-readout")!;
  alpha.addEventListener("input", () => {
    const n = Number(alpha.value) | 0;
    readout.textContent = `${Math.round((n / 255) * 100)}%`;
    saveSettings({ ...loadSettings(), previewAlpha: n });
  });
  card.appendChild(transparency);
  wrap.appendChild(card);

  wrap.appendChild(eyebrow("Sharing"));
  const sharing = settingsCard();
  sharing.classList.add("settings-card-padded");
  // Resize-before-share toggle + slider.
  const resizeRow = document.createElement("div");
  resizeRow.className = "settings-row settings-row-inline";
  resizeRow.innerHTML = `
    <div>
      <div class="settings-row-title serif">Resize before share</div>
      <div class="settings-row-meta">Shrink the redacted image before handing it to the system share sheet. Useful when receivers limit attachment size.</div>
    </div>
    <label class="settings-toggle">
      <input id="resize-toggle" type="checkbox" />
      <span class="settings-toggle-track"><span class="settings-toggle-thumb"></span></span>
    </label>
  `;
  const resizeToggle = resizeRow.querySelector<HTMLInputElement>("#resize-toggle")!;
  resizeToggle.checked = s.resizeBeforeShare;
  sharing.appendChild(resizeRow);
  const pctRow = document.createElement("div");
  pctRow.className = "settings-row settings-row-block";
  pctRow.innerHTML = `
    <div class="settings-section-head settings-section-head-row">
      <div class="settings-row-title serif">Resize percent</div>
      <div class="settings-section-body settings-section-body-mono" id="pct-readout">${s.resizeSharePercent}%</div>
    </div>
    <input id="resize-pct" type="range" min="10" max="90" step="10" value="${s.resizeSharePercent}" class="settings-slider" />
  `;
  const pctSlider = pctRow.querySelector<HTMLInputElement>("#resize-pct")!;
  const pctReadout = pctRow.querySelector<HTMLDivElement>("#pct-readout")!;
  const updatePctEnabled = () => {
    pctSlider.disabled = !resizeToggle.checked;
    pctRow.classList.toggle("is-disabled", !resizeToggle.checked);
  };
  resizeToggle.addEventListener("change", () => {
    saveSettings({ ...loadSettings(), resizeBeforeShare: resizeToggle.checked });
    updatePctEnabled();
  });
  pctSlider.addEventListener("input", () => {
    pctReadout.textContent = `${pctSlider.value}%`;
    saveSettings({ ...loadSettings(), resizeSharePercent: Number(pctSlider.value) | 0 });
  });
  updatePctEnabled();
  sharing.appendChild(pctRow);
  wrap.appendChild(sharing);

  wrap.appendChild(eyebrow("Debug"));
  const debug = settingsCard();
  debug.classList.add("settings-card-padded");
  const debugRow = document.createElement("div");
  debugRow.className = "settings-row settings-row-inline";
  debugRow.innerHTML = `
    <div>
      <div class="settings-row-title serif">Show OCR detections</div>
      <div class="settings-row-meta">Overlays each token Tesseract recognised on top of the editor canvas. Useful when a redaction looks off — you can see whether OCR mis-read the text or the privacy filter mis-classified it.</div>
    </div>
    <label class="settings-toggle">
      <input id="debug-ocr-toggle" type="checkbox" />
      <span class="settings-toggle-track"><span class="settings-toggle-thumb"></span></span>
    </label>
  `;
  const debugToggle = debugRow.querySelector<HTMLInputElement>("#debug-ocr-toggle")!;
  debugToggle.checked = s.debugShowOcr;
  debugToggle.addEventListener("change", () => {
    saveSettings({ ...loadSettings(), debugShowOcr: debugToggle.checked });
  });
  debug.appendChild(debugRow);
  wrap.appendChild(debug);

  return wrap;
}

function renderSettingsInfo(): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "settings-tab-pane";

  wrap.appendChild(eyebrow("Runtime"));
  const runtime = settingsCard();
  runtime.classList.add("settings-card-padded");
  runtime.innerHTML = `<p class="settings-section-body" id="runtime-loading">Checking GPU availability…</p>`;
  wrap.appendChild(runtime);
  // Async fill — we await the WebGPU adapter, which can take ~50ms.
  void getRuntimeStatus().then((status) => fillRuntimeCard(runtime, status));

  wrap.appendChild(eyebrow("About"));
  const about = settingsCard();
  about.classList.add("settings-card-padded");
  about.innerHTML = `
    <div class="settings-section-head">
      <div class="settings-section-title settings-about-headline serif">Share images, not what's in them.</div>
      <p class="settings-section-body">Redact runs OCR on your image, then a small open-source model called Privacy Filter flags names, addresses, account numbers, and other sensitive spans.</p>
      <p class="settings-section-body">You see exactly what would be hidden before you share. Add or edit boxes by hand if anything looks off.</p>
    </div>
    <ul class="settings-bullets">
      <li>Every model runs locally in your browser.</li>
      <li>No account, no telemetry, no upload.</li>
      <li>You decide what gets covered before sharing.</li>
    </ul>
    <div class="settings-about-actions">
      <a class="pill pill-ghost" href="https://github.com/eordano/redact-pii" target="_blank" rel="noopener">Source</a>
      <a class="pill pill-ghost" href="https://github.com/eordano/redact-pii/blob/main/LICENSE" target="_blank" rel="noopener">License</a>
    </div>
  `;
  wrap.appendChild(about);

  return wrap;
}

function fillRuntimeCard(card: HTMLElement, status: RuntimeStatus): void {
  const onGpu = status.webgpu.adapterReady;
  const engine = summariseEPs(status);
  const { persisted, quotaBytes, usageBytes } = status.storage;

  card.innerHTML = "";
  card.appendChild(infoRow("Engine", engine));
  card.appendChild(infoRow("Threads", String(status.numThreads)));
  if (onGpu && status.webgpu.adapterName) {
    card.appendChild(infoRow("Adapter", status.webgpu.adapterName));
  }

  if (persisted !== null) {
    const usage = quotaBytes && usageBytes !== null
      ? `${formatBytes(usageBytes)} of ${formatBytes(quotaBytes)}`
      : "";
    const label = persisted ? "Persistent" : "Best-effort";
    const value = usage ? `${label} · ${usage}` : label;
    const trail = persisted ? null : renderPersistentStorageButton(card);
    card.appendChild(infoRow("Storage", value, trail));
  }
}

/** Render a labeled `Engine: WebGPU`-style row that matches Editing's
 *  inline-row vocabulary. The optional trail element sits on the right
 *  next to the value (e.g. for an action button). */
function infoRow(label: string, value: string, trail: HTMLElement | null = null): HTMLElement {
  const row = document.createElement("div");
  row.className = "settings-row settings-row-inline";
  const left = document.createElement("div");
  left.innerHTML = `<div class="settings-row-title serif">${escapeHtml(label)}</div>`;
  const right = document.createElement("div");
  right.className = "settings-row-trail";
  right.textContent = value;
  if (trail) right.appendChild(trail);
  row.appendChild(left);
  row.appendChild(right);
  return row;
}

function renderPersistentStorageButton(card: HTMLElement): HTMLElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "pill pill-ghost";
  btn.textContent = "Request";
  btn.style.marginLeft = "10px";
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    const granted = await requestPersistentStorage();
    if (granted) {
      const next = await getRuntimeStatus();
      fillRuntimeCard(card, next);
    } else {
      btn.disabled = false;
      btn.textContent = "Declined";
    }
  });
  return btn;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

interface SelectorOpt {
  id: string;
  label: string;
  description?: string;
  badge?: string | null;
}

function renderSelectorList(
  options: SelectorOpt[],
  selected: string,
  onChange: (id: string) => void,
  opts: { showBadges?: boolean } = {},
): HTMLElement {
  const list = document.createElement("div");
  list.className = "settings-list";
  for (const o of options) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "settings-list-row" + (o.id === selected ? " is-selected" : "");
    row.innerHTML = `
      <div class="settings-list-row-body">
        <div class="settings-list-row-head">
          <span class="settings-list-row-label serif">${escapeHtml(o.label)}</span>
          ${opts.showBadges && o.badge ? `<span class="settings-pill ${o.badge === "Installed" ? "settings-pill-installed" : ""}">${escapeHtml(o.badge)}</span>` : ""}
        </div>
        ${o.description ? `<div class="settings-list-row-desc">${escapeHtml(o.description)}</div>` : ""}
      </div>
      <div class="settings-list-row-radio" aria-hidden="true">${o.id === selected ? "●" : ""}</div>
    `;
    row.addEventListener("click", () => onChange(o.id));
    list.appendChild(row);
  }
  return list;
}

/* -------------------------------------------------------------------------- */
/* Helpers.                                                                   */
/* -------------------------------------------------------------------------- */

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c] ?? c));
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}
